import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3)
    : [];

  if (!article) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm cursor-pointer">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const renderContent = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      if (!line.trim()) {
        i++;
        continue;
      }

      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="text-xl font-heading font-bold text-foreground-950 mt-10 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="text-lg font-semibold text-foreground-900 mt-8 mb-3">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('## 结语')) {
        elements.push(
          <h2 key={i} className="text-xl font-heading font-bold text-foreground-950 mt-10 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
      } else {
        elements.push(
          <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
            {line}
          </p>
        );
      }
      i++;
    }
    return elements;
  };

  const serviceNameMap: Record<string, string> = {
    'domain-registration': '域名注册',
    'website-building': '网站建设',
    'cloud-hosting': '云主机',
    'enterprise-email': '企业邮箱',
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 91.cn`}
        description={article.summary.substring(0, 150)}
        keywords={`91.cn,${article.title},${serviceNameMap[article.category] || ''},互联网服务`}
        canonicalUrl={`https://www.91.cn/article/${article.slug}`}
        ogType="article"
      />
      <main>
        <div className="w-full px-4 md:px-6 lg:px-10 pt-24 pb-8 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: serviceNameMap[article.category] || '服务', href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
              <div className="lg:col-span-2">
                <header className="mb-10">
                  <div className="flex items-center gap-3 mb-4">
                    <Link
                      to={`/category/${article.category}`}
                      className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap cursor-pointer"
                    >
                      {serviceNameMap[article.category]}
                    </Link>
                    <span className="text-xs text-foreground-400">{article.date}</span>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-tight mb-3">
                    {article.title}
                  </h1>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    {article.summary}
                  </p>
                </header>

                <div className="prose-custom">
                  {renderContent(article.content)}
                </div>
              </div>

              <aside className="lg:col-span-1">
                <div className="sticky top-36">
                  <h3 className="text-lg font-semibold text-foreground-900 mb-4">
                    相关文章
                  </h3>
                  <div className="flex flex-col gap-3">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.slug}
                        to={`/article/${ra.slug}`}
                        className="group p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                      >
                        <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors mb-1 line-clamp-2">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-500 line-clamp-2 mb-2">
                          {ra.summary}
                        </p>
                        <span className="text-xs text-foreground-400">{ra.date}</span>
                      </Link>
                    ))}
                    {relatedArticles.length === 0 && (
                      <p className="text-sm text-foreground-500">暂无相关文章</p>
                    )}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <div className="w-full px-4 md:px-6 lg:px-10 py-12 bg-background-100">
            <div className="max-w-7xl mx-auto text-center">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                查看更多{serviceNameMap[article.category] || ''}相关文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        )}
      </main>
    </>
  );
}